class Product360Card extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.baseUrl = this.dataset.frameBaseUrl;
    this.totalFrames = parseInt(this.dataset.totalFrames) || 50;
    this.frameStart = parseInt(this.dataset.frameStart) || 1;
    this.extension = this.dataset.frameExtension || 'jpg';
    this.framePadding = parseInt(this.dataset.framePadding) || 2;

    // Get rotation sensitivity from CSS custom property (section-level setting)
    const sensitivity = getComputedStyle(document.documentElement)
      .getPropertyValue('--rotation-sensitivity-360-details');
    this.rotationSensitivity = sensitivity ? parseInt(sensitivity) : 10;

    // State
    this.currentFrame = 1;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartFrame = 1;
    this.loadedFrames = new Set();
    this.frameCache = {};

    // DOM elements
    this.canvas = this.querySelector('[data-rotation-canvas]');
    this.frameImage = this.querySelector('[data-frame-image]');
    this.loadingIndicator = this.querySelector('[data-loading-indicator]');

    // Initialize
    this.init();
  }

  init() {
    if (!this.baseUrl || !this.canvas || !this.frameImage || !this.loadingIndicator) {
      console.warn('Missing required elements for product-360-card');
      return;
    }

    // Start loading frames
    this.loadFramesProgressive();

    // Setup rotation events
    this.setupRotationEvents();
  }

  loadFramesProgressive() {
    // Priority frames: 1, middle, last
    const priorityFrames = [1, Math.floor(this.totalFrames / 2), this.totalFrames];

    // Load priority frames first
    Promise.all(priorityFrames.map(frameNum => this.loadFrame(frameNum)))
      .then(() => {
        // Set first frame immediately
        if (this.frameCache[1]) {
          this.frameImage.src = this.frameCache[1].src;
          this.currentFrame = 1;
        }

        // Dim loading indicator after priority frames load
        this.loadingIndicator.style.opacity = '0.5';

        // Load remaining frames in background
        this.loadRemainingFrames();
      })
      .catch(err => {
        console.error('Failed to load priority frames:', err);
        this.loadingIndicator.innerHTML = '<p style="color: red; font-size: 14px;">Failed to load images</p>';
      });
  }

  loadRemainingFrames() {
    const priorityFrames = [1, Math.floor(this.totalFrames / 2), this.totalFrames];
    const remainingFrames = [];

    for (let i = this.frameStart; i <= this.totalFrames; i++) {
      if (!priorityFrames.includes(i)) {
        remainingFrames.push(i);
      }
    }

    // Load in batches using requestIdleCallback
    const loadNextBatch = (startIdx) => {
      const batchSize = 5;
      const batch = remainingFrames.slice(startIdx, startIdx + batchSize);

      if (batch.length === 0) {
        // All frames loaded
        this.loadingIndicator.classList.add('hidden');
        return;
      }

      Promise.all(batch.map(frameNum => {
        return this.loadFrame(frameNum)
          .catch(err => console.warn(`Frame ${frameNum} failed to load:`, err));
      }))
      .then(() => {
        // Schedule next batch
        if (window.requestIdleCallback) {
          requestIdleCallback(() => loadNextBatch(startIdx + batchSize));
        } else {
          setTimeout(() => loadNextBatch(startIdx + batchSize), 50);
        }
      });
    };

    loadNextBatch(0);
  }

  loadFrame(frameNumber) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const paddedNumber = frameNumber.toString().padStart(this.framePadding, '0');
      const url = `${this.baseUrl}_${paddedNumber}.${this.extension}`;

      img.onload = () => {
        this.frameCache[frameNumber] = img;
        this.loadedFrames.add(frameNumber);
        resolve(img);
      };

      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  }

  setupRotationEvents() {
    // Determine if mobile for sensitivity adjustment
    const isMobile = () => window.innerWidth <= 768;

    // Mouse events (desktop)
    this.canvas.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartFrame = this.currentFrame;
      this.canvas.classList.add('is-dragging');
    });

    const handleMouseMove = (e) => {
      if (!this.isDragging) return;

      const dragDistance = e.clientX - this.dragStartX;
      const sensitivity = isMobile() ? this.rotationSensitivity * 0.8 : this.rotationSensitivity;
      const frameDelta = Math.floor(dragDistance / sensitivity);

      // Calculate new frame (with wrapping)
      let newFrame = this.dragStartFrame + frameDelta;
      newFrame = ((newFrame - 1) % this.totalFrames + this.totalFrames) % this.totalFrames + 1;

      if (newFrame !== this.currentFrame) {
        this.updateFrame(newFrame);
      }
    };

    const handleMouseUp = () => {
      if (this.isDragging) {
        this.isDragging = false;
        this.canvas.classList.remove('is-dragging');
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Touch events (mobile)
    let touchStartX = 0;

    this.canvas.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      this.dragStartX = touchStartX;
      this.dragStartFrame = this.currentFrame;
      this.isDragging = true;
      this.canvas.classList.add('is-dragging');
    }, { passive: true });

    this.canvas.addEventListener('touchmove', (e) => {
      if (!this.isDragging) return;

      const touchX = e.touches[0].clientX;
      const dragDistance = touchX - this.dragStartX;
      const sensitivity = this.rotationSensitivity * 0.8; // More sensitive on mobile
      const frameDelta = Math.floor(dragDistance / sensitivity);

      let newFrame = this.dragStartFrame + frameDelta;
      newFrame = ((newFrame - 1) % this.totalFrames + this.totalFrames) % this.totalFrames + 1;

      if (newFrame !== this.currentFrame) {
        this.updateFrame(newFrame);
      }
    }, { passive: true });

    this.canvas.addEventListener('touchend', () => {
      if (this.isDragging) {
        this.isDragging = false;
        this.canvas.classList.remove('is-dragging');
      }
    }, { passive: true });
  }

  updateFrame(frameNumber) {
    this.currentFrame = frameNumber;

    if (this.frameCache[frameNumber]) {
      this.frameImage.src = this.frameCache[frameNumber].src;
    } else {
      console.warn(`Frame ${frameNumber} not loaded yet`);
    }
  }
}

// Register custom element
customElements.define('product-360-card', Product360Card);
